import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import indianPayQrCode from "../../assets/indianpayqrcode.jpeg";
import {
  initiateIndianPayment,
  submitIndianPayment,
} from "../../utils/indianPayment";
import { getServiceLabel } from "../../utils/careAccess";

const IndianQrPaymentModal = ({
  open,
  onClose,
  service,
  onSuccess,
}) => {
  const [step, setStep] = useState("qr");
  const [customerId, setCustomerId] = useState("");
  const [transactionNumber, setTransactionNumber] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setStep("qr");
      setCustomerId("");
      setTransactionNumber("");
      setReceiptFile(null);
      setLoading(false);
      setError("");
    }
  }, [open]);

  const handleContinueToProof = async () => {
    if (!service) {
      setError("No service selected for payment.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const data = await initiateIndianPayment(service);
      setCustomerId(data.customerId);
      setStep("proof");
    } catch (initError) {
      setError(
        initError.response?.data?.error ||
          initError.message ||
          "Unable to start QR payment."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitProof = async () => {
    if (!transactionNumber.trim() && !receiptFile) {
      setError("Enter a transaction number or upload a payment receipt.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await submitIndianPayment({
        serviceId: service._id,
        customerId,
        transactionNumber,
        receiptFile,
      });
      onSuccess?.();
      onClose();
    } catch (submitError) {
      setError(
        submitError.response?.data?.error ||
          submitError.message ||
          "Unable to submit payment proof."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {step === "qr" ? "Scan to pay" : "Confirm your payment"}
      </DialogTitle>
      <DialogContent>
        {service && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {getServiceLabel(service)} — {service.careFees} {service.currency}
          </Typography>
        )}

        {step === "qr" ? (
          <Box className="indian-payment-qr-step">
            <Typography variant="body1" sx={{ mb: 2 }}>
              Scan the QR code below using your UPI or banking app to complete
              the payment.
            </Typography>
            <Box className="indian-payment-qr-wrap">
              <img
                src={indianPayQrCode}
                alt="Indian payment QR code"
                className="indian-payment-qr-image"
              />
            </Box>
          </Box>
        ) : (
          <Box className="indian-payment-proof-step">
            <Typography variant="body1" sx={{ mb: 2 }}>
              After paying, enter your transaction number or upload the payment
              receipt to activate your care plan.
            </Typography>
            <TextField
              fullWidth
              label="Transaction number"
              value={transactionNumber}
              onChange={(event) => setTransactionNumber(event.target.value)}
              margin="normal"
            />
            <Button variant="outlined" component="label" fullWidth sx={{ mt: 2 }}>
              {receiptFile ? receiptFile.name : "Upload payment receipt"}
              <input
                hidden
                type="file"
                accept="image/*,.pdf"
                onChange={(event) =>
                  setReceiptFile(event.target.files?.[0] || null)
                }
              />
            </Button>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        {step === "qr" ? (
          <Button
            variant="contained"
            color="success"
            onClick={handleContinueToProof}
            disabled={loading || !service}
            startIcon={
              loading ? <CircularProgress size={18} color="inherit" /> : null
            }
          >
            {loading ? "Preparing..." : "I've paid — continue"}
          </Button>
        ) : (
          <>
            <Button onClick={() => setStep("qr")} disabled={loading}>
              Back
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleSubmitProof}
              disabled={loading}
              startIcon={
                loading ? <CircularProgress size={18} color="inherit" /> : null
              }
            >
              {loading ? "Submitting..." : "Submit payment proof"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default IndianQrPaymentModal;
